import { createTheme } from '@mui/material/styles';

declare module '@mui/material/styles/createTheme' {
  interface Theme {
    status: {
      danger: string;
    };
  }
  // allow configuration using `createTheme`
  interface DeprecatedThemeOptions {
    status?: {
      danger?: string;
    };
  }
}

/**
 * MUI surfaces read CSS vars from `html[data-quickcam-scheme]` /
 * `html[data-quickcam-theme]` so Dialogs, Menus, Drawers, and Selects
 * follow Appearance preferences without rebuilding the theme object.
 */
const qc = {
  accent: 'var(--qc-accent, #2a9dff)',
  accent2: 'var(--qc-accent-2, #9d5cff)',
  accentSoft: 'var(--qc-accent-soft, rgba(42, 157, 255, 0.18))',
  accentLine: 'var(--qc-accent-line, rgba(91, 151, 255, 0.28))',
  bg: 'var(--qc-shell-bg, #020712)',
  surface: 'var(--qc-shell-surface, #071426)',
  surfaceStrong: 'var(--qc-shell-surface-strong, #091a31)',
  copy: 'var(--qc-shell-copy, #f8f9ff)',
  muted: 'var(--qc-shell-muted, #a9b5ca)',
};

const paperSurface = {
  backgroundColor: `var(--qc-shell-fg, ${qc.surface}) !important`,
  backgroundImage: 'none !important',
  color: `${qc.copy} !important`,
  border: `1px solid ${qc.accentLine}`,
  borderRadius: 'var(--qc-radius-card, 20px)',
};

const theme = createTheme({
  palette: {
    // Palette values MUST be concrete colors — MUI createTheme calls
    // lighten()/decomposeColor() at build time and crashes on CSS vars
    // (Error: Unsupported `var(--qc-accent, …)`), leaving the HTML loader up.
    // Live Appearance colors still apply via `qc.*` CSS vars in component
    // styleOverrides and dashboard-preferences.scss.
    mode: 'dark',
    primary: {
      main: '#2a9dff',
    },
    secondary: {
      main: '#9d5cff',
    },
    background: {
      default: '#020712',
      paper: '#091a31',
    },
    text: {
      primary: '#f8f9ff',
      secondary: '#a9b5ca',
      disabled: 'rgba(248, 249, 255, 0.38)',
    },
    divider: 'rgba(91, 151, 255, 0.28)',
    action: {
      hover: 'rgba(42, 157, 255, 0.18)',
      selected: 'rgba(42, 157, 255, 0.18)',
      disabled: 'rgba(248, 249, 255, 0.3)',
      disabledBackground: 'rgba(248, 249, 255, 0.08)',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: qc.bg,
          color: qc.copy,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: paperSurface,
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          ...paperSurface,
          backgroundColor: `${qc.surface} !important`,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          color: qc.copy,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          color: qc.copy,
        },
      },
    },
    MuiDialogContentText: {
      styleOverrides: {
        root: {
          color: qc.muted,
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          borderTop: `1px solid ${qc.accentLine}`,
        },
      },
    },
    MuiModal: {
      styleOverrides: {
        root: {
          '&.MuiModal-hidden': {
            pointerEvents: 'none',
            visibility: 'hidden',
          },
        },
        backdrop: {
          '.MuiModal-hidden &': {
            pointerEvents: 'none',
            opacity: 0,
          },
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          '.MuiModal-hidden &': {
            pointerEvents: 'none',
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: paperSurface,
        list: {
          backgroundColor: 'transparent',
          color: qc.copy,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: qc.copy,
          '&:hover': {
            backgroundColor: qc.accentSoft,
          },
          '&.Mui-selected': {
            backgroundColor: qc.accentSoft,
            '&:hover': {
              backgroundColor: qc.accentSoft,
            },
          },
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: paperSurface,
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          ...paperSurface,
          backgroundColor: `${qc.surface} !important`,
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundColor: `${qc.surface} !important`,
          color: qc.copy,
          border: `1px solid ${qc.accentLine}`,
          boxShadow: 'none',
          '&:before': {
            display: 'none',
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          color: qc.copy,
        },
        expandIconWrapper: {
          color: qc.muted,
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          color: qc.muted,
          borderTop: `1px solid ${qc.accentLine}`,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        icon: {
          color: qc.muted,
        },
      },
    },
    MuiSnackbarContent: {
      styleOverrides: {
        root: {
          backgroundColor: `${qc.surfaceStrong} !important`,
          color: `${qc.copy} !important`,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          border: `1px solid ${qc.accentLine}`,
        },
        standardInfo: {
          backgroundColor: qc.surfaceStrong,
          color: qc.copy,
        },
        standardSuccess: {
          backgroundColor: qc.surfaceStrong,
          color: qc.copy,
        },
        standardWarning: {
          backgroundColor: qc.surfaceStrong,
          color: qc.copy,
        },
        standardError: {
          backgroundColor: qc.surfaceStrong,
          color: qc.copy,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: qc.surfaceStrong,
          color: qc.copy,
          border: `1px solid ${qc.accentLine}`,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: qc.accentLine,
          color: qc.copy,
        },
        head: {
          color: qc.muted,
          backgroundColor: qc.surfaceStrong,
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          backgroundColor: qc.surface,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'capitalize',
          fontWeight: 600,
        },
        containedPrimary: {
          backgroundColor: 'var(--qc-btn-bg, #3b6ea5)',
          backgroundImage: 'none',
          color: 'var(--qc-btn-fg, #f8fafc)',
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: 'var(--qc-btn-bg-hover, #335f8f)',
            backgroundImage: 'none',
            boxShadow: 'none',
          },
        },
        outlined: {
          borderColor: qc.accentLine,
          color: qc.copy,
        },
        text: {
          color: qc.accent,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          color: qc.copy,
          backgroundColor: qc.surfaceStrong,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: qc.accentLine,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: qc.accent,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: qc.accent,
          },
          '&.Mui-disabled': {
            WebkitTextFillColor: 'rgba(248, 249, 255, 0.45)',
            color: 'rgba(248, 249, 255, 0.45)',
          },
        },
        input: {
          color: qc.copy,
          '&::placeholder': {
            color: qc.muted,
            opacity: 1,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: qc.muted,
          '&.Mui-focused': {
            color: qc.accent,
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: qc.muted,
          '&.Mui-disabled': {
            color: 'rgba(248, 249, 255, 0.45)',
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          color: qc.muted,
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: qc.muted,
          '&.Mui-checked': {
            color: qc.accent,
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: qc.muted,
          '&.Mui-checked': {
            color: qc.accent,
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          color: qc.muted,
          '&.Mui-checked': {
            color: qc.accent,
          },
          '&.Mui-checked + .MuiSwitch-track': {
            backgroundColor: qc.accent,
          },
        },
        track: {
          backgroundColor: qc.accentLine,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: qc.accent,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          padding: '8px',
          textTransform: 'capitalize',
          color: qc.muted,
          '&.Mui-selected': {
            color: qc.accent,
          },
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          padding: '8px',
          textTransform: 'capitalize',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: qc.muted,
          '&:hover': {
            backgroundColor: qc.accentSoft,
            color: qc.copy,
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: qc.accentLine,
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          color: qc.copy,
        },
        secondary: {
          color: qc.muted,
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          // Prefer inheriting page/shell color; avoid forcing white on light schemes.
        },
      },
    },
  },
});

export { theme };
